import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Save, RefreshCw } from "lucide-react";

const BUDGET_SUB_KEYS = ['budget_item_created', 'budget_item_updated', 'budget_item_deleted'];

const EVENT_TYPES = [
  { key: 'status_change', label: 'Status Changes' },
  { key: 'timing_change', label: 'Timing/Deadline Changes' },
  { key: 'pulse_deletion', label: 'Pulse Deletions' },
  { key: 'artist_change', label: 'Artist Assignment Changes' },
  { key: 'budget_updates', label: 'Budget/Financial Updates', composite: BUDGET_SUB_KEYS },
];

export function AdminPulseSettings() {
  const { toast } = useToast();

  const { data: levelDefs, isLoading } = useQuery({
    queryKey: ['/api/admin-pulses/level-definitions'],
  });

  const [level1Events, setLevel1Events] = useState<string[]>([]);
  const [level2Events, setLevel2Events] = useState<string[]>([]);

  useEffect(() => {
    if (levelDefs) {
      const defs = levelDefs as any;
      setLevel1Events(defs.level_1 || []);
      setLevel2Events(defs.level_2 || []);
    }
  }, [levelDefs]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('PUT', '/api/admin-pulses/level-definitions', {
        level_1: level1Events,
        level_2: level2Events,
      });
      if (!response.ok) throw new Error('Failed to save');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin-pulses/level-definitions'] });
      toast({ title: "Saved", description: "Admin pulse level definitions updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save settings.", variant: "destructive" });
    },
  });

  const resolveCurrentLevel = (eventConfig: typeof EVENT_TYPES[number]): string => {
    if (eventConfig.composite) {
      const anyInLevel2 = eventConfig.composite.some(k => level2Events.includes(k));
      if (anyInLevel2) return 'level_2';
      return 'level_1';
    }
    return level1Events.includes(eventConfig.key) ? 'level_1' : 'level_2';
  };

  const handleLevelChange = (eventConfig: typeof EVENT_TYPES[number], level: string) => {
    const keys = eventConfig.composite || [eventConfig.key];
    setLevel1Events(prev => prev.filter(e => !keys.includes(e)));
    setLevel2Events(prev => prev.filter(e => !keys.includes(e)));
    if (level === 'level_1') {
      setLevel1Events(prev => [...prev, ...keys]);
    } else {
      setLevel2Events(prev => [...prev, ...keys]);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-400">Loading settings...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-400 mb-3">
        Configure which event types generate Level 1 (manual/important) vs Level 2 (system/auto) admin pulses.
      </div>

      <div className="space-y-2">
        {EVENT_TYPES.map((evt) => {
          const currentLevel = resolveCurrentLevel(evt);
          return (
            <div key={evt.key} className="flex items-center justify-between py-1 px-2 rounded bg-gray-800">
              <Label className="text-sm text-gray-300">{evt.label}</Label>
              <Select value={currentLevel} onValueChange={(val) => handleLevelChange(evt, val)}>
                <SelectTrigger className="w-32 h-7 text-xs bg-gray-900 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="level_1">Level 1</SelectItem>
                  <SelectItem value="level_2">Level 2</SelectItem>
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end pt-2">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          size="sm"
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          {saveMutation.isPending ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
}
